/**
 * BulkImportWellsModal — Phase 24 / Task 24.11 (DEC-104).
 *
 * 4-step wizard (manager-only) to import platform wells for one operator as
 * standalone-well tanks. Wells already in a tank battery or already carrying a
 * standalone tank for this tenant are shown disabled (and the server skips them
 * defensively). A standard tank can optionally be applied to every selected well.
 *
 * Steps: 1 select operator (+ state) → 2 select wells → 3 optional tank →
 * 4 confirm + import. The bulk endpoint returns 200 with {imported, skipped,
 * skippedReasons} (the web-shared fetch wrappers can't surface error bodies, so
 * per-well outcomes ride the success payload).
 */
import { useExpressCustomerState } from '../../lib/express-customer';
import * as React from 'react';
import { standardTankLabel } from '@lodex-pro/shared/tank-strapping';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { fieldStyle, labelStyle, FormRow } from './form-bits';
import { subText } from './ui-bits';
import {
  useHaulerOperators,
  useOperatorWells,
  useBulkImportWells,
  useStandardTanks,
  type OperatorWell,
  type BulkImportResult,
  type BulkImportBody,
} from './hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

const STATES = ['OK', 'TX', 'WY', 'NM', 'ND'] as const;

/** Stable empty array so the wells-derived useMemo deps don't change each render. */
const EMPTY_WELLS: readonly OperatorWell[] = [];

function toNum(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

type Step = 1 | 2 | 3 | 4;

export function BulkImportWellsModal({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [step, setStep] = React.useState<Step>(1);
  const [operatorId, setOperatorId] = React.useState('');
  const [stateFilter, setStateFilter] = React.useState('');
  const [loadedOperatorId, setLoadedOperatorId] = React.useState('');
  const [loadedState, setLoadedState] = React.useState('');
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [filter, setFilter] = React.useState('');
  const [countyFilter, setCountyFilter] = React.useState('');

  const [applyTank, setApplyTank] = React.useState(false);
  const [standardId, setStandardId] = React.useState('');
  const [tankName, setTankName] = React.useState('');
  const [tankType, setTankType] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [bblPerInch, setBblPerInch] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  const [result, setResult] = React.useState<BulkImportResult | null>(null);
  const [importError, setImportError] = React.useState<string | null>(null);
  // 86-followup #2 — chunked import progress ("Importing X of Y wells…").
  const [importing, setImporting] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });

  const { data: opsData } = useHaulerOperators();
  // §58 PR 4 — on EXPRESS, only the one customer's wells can be added.
  const { express, pending: expressPending } = useExpressCustomerState();
  const operators = (opsData?.haulerOperators ?? []).filter(
    (o) =>
      o.isActive &&
      o.operatorId !== null &&
      (express === null || o.operatorId === express.operatorId),
  );
  React.useEffect(() => {
    if (express !== null && operatorId === '') setOperatorId(express.operatorId);
  }, [express, operatorId]);
  const { data: wellsData, isFetching: wellsLoading } = useOperatorWells(
    loadedOperatorId,
    loadedState,
  );
  const wells = wellsData?.wells ?? EMPTY_WELLS;
  const { data: stdData } = useStandardTanks();
  const standardTanks = (stdData?.standardTanks ?? []).filter((s) => s.isActive);

  // onDone is a no-op — we drive a CHUNKED import (below) and aggregate the
  // per-chunk results ourselves so the modal can show real "X of Y" progress.
  const bulkImport = useBulkImportWells(() => {});

  const reset = React.useCallback((): void => {
    setStep(1);
    setOperatorId('');
    setStateFilter('');
    setLoadedOperatorId('');
    setLoadedState('');
    setSelected(new Set());
    setFilter('');
    setCountyFilter('');
    setApplyTank(false);
    setStandardId('');
    setTankName('');
    setTankType('');
    setCapacity('');
    setBblPerInch('');
    setFormError(null);
    setResult(null);
    setImportError(null);
    setImporting(false);
    setProgress({ done: 0, total: 0 });
  }, []);

  React.useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const operatorName =
    operators.find((o) => o.operatorId === loadedOperatorId)?.resolvedName ?? 'Operator';

  // Counties present in the loaded wells (Step-2 county dropdown).
  const counties = React.useMemo(
    () =>
      [
        ...new Set(wells.map((w) => w.county).filter((c): c is string => c != null && c !== '')),
      ].sort((a, b) => a.localeCompare(b)),
    [wells],
  );

  const visibleWells = wells.filter((w) => {
    if (countyFilter !== '' && w.county !== countyFilter) return false;
    if (filter.trim() === '') return true;
    const q = filter.trim().toLowerCase();
    return (
      w.wellName.toLowerCase().includes(q) ||
      w.apiNumber.toLowerCase().includes(q) ||
      (w.county ?? '').toLowerCase().includes(q)
    );
  });
  const availableVisible = visibleWells.filter((w) => !w.hasHaulerWells && !w.inTankBattery);

  // 86-followup #1 — selection-clarity display values (logic unchanged; the
  // selection set itself is filter-independent and additive). "Here" = the current
  // filter view; "hidden" = selected wells not shown by the active county/text filter.
  const visibleIds = new Set(visibleWells.map((w) => w.id));
  const selectedHere = [...selected].filter((id) => visibleIds.has(id)).length;
  const hiddenSelected = selected.size - selectedHere;
  const countyLabel = countyFilter !== '' ? ` in ${countyFilter}` : '';
  const filterActive = countyFilter !== '' || filter.trim() !== '';

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildTank = (): BulkImportBody['tank'] => {
    if (!applyTank) return undefined;
    const cap = toNum(capacity);
    const bpi = toNum(bblPerInch);
    return {
      name: tankName.trim(),
      ...(cap !== undefined && { capacityBbl: cap }),
      ...(bpi !== undefined && { bblPerInch: bpi }),
      ...(tankType.trim() !== '' && { tankType: tankType.trim() }),
      ...(standardId !== '' && { standardTankCatalogId: standardId }),
    };
  };

  // Chunked import (86-followup #2): the API accepts any number of wells in one
  // call, but we send batches of 50 so the modal can show real "Importing X of Y"
  // progress and each request stays small. Per-chunk results are aggregated.
  const CHUNK = 50;
  const doImport = (): void => {
    if (applyTank && tankName.trim() === '') {
      setFormError('Tank name is required when applying a tank.');
      setStep(3);
      return;
    }
    const all = [...selected];
    const tank = buildTank();
    setImportError(null);
    setImporting(true);
    setProgress({ done: 0, total: all.length });

    void (async (): Promise<void> => {
      const acc = { imported: 0, skipped: 0, skippedReasons: [] as string[] };
      try {
        for (let i = 0; i < all.length; i += CHUNK) {
          const chunk = all.slice(i, i + CHUNK);
          const r = await bulkImport.mutateAsync({
            wellIds: chunk,
            ...(tank !== undefined && { tank }),
          });
          acc.imported += r.imported;
          acc.skipped += r.skipped;
          acc.skippedReasons.push(...r.skippedReasons);
          setProgress({ done: Math.min(i + CHUNK, all.length), total: all.length });
        }
        setResult(acc);
      } catch {
        setImportError(
          'Import stopped partway through. Reopen Import Wells to add the remaining wells.',
        );
        if (acc.imported > 0 || acc.skipped > 0) setResult(acc);
      } finally {
        setImporting(false);
      }
    })();
  };

  const onPickStandard = (id: string): void => {
    setStandardId(id);
    const std = standardTanks.find((s) => s.id === id);
    if (std) {
      setCapacity(std.capacityBbl);
      setBblPerInch(std.bblPerInch);
      if (tankName.trim() === '') setTankName(std.name);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '720px' }}>
        <DialogHeader>
          <DialogTitle>Import wells {step < 4 ? `· Step ${step} of 3` : ''}</DialogTitle>
        </DialogHeader>

        {/* ── STEP 1 — select operator ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <FormRow label="Operator" htmlFor="bulk-op" required>
              <Combobox
                id="bulk-op"
                options={operators.map((o) => ({
                  value: o.operatorId ?? '',
                  label: `${o.resolvedName} (${o.ticketAbbreviation})`,
                }))}
                value={operatorId}
                onChange={setOperatorId}
                disabled={express !== null || expressPending}
                placeholder="Select an operator…"
                searchPlaceholder="Search operators…"
                emptyText="No operators. Add one on the Operators tab first."
              />
            </FormRow>
            <FormRow label="State (optional)" htmlFor="bulk-state">
              <select
                id="bulk-state"
                value={stateFilter}
                onChange={(e): void => setStateFilter(e.target.value)}
                style={fieldStyle}
              >
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FormRow>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={operatorId === ''}
                onClick={(): void => {
                  setLoadedOperatorId(operatorId);
                  setLoadedState(stateFilter);
                  setSelected(new Set());
                  setStep(2);
                }}
                style={primaryBtn}
              >
                Load Wells
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── STEP 2 — select wells ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={subText}>
              {wellsLoading
                ? 'Loading wells…'
                : `Found ${wells.length} well${wells.length === 1 ? '' : 's'} for ${operatorName}${
                    loadedState !== '' ? ` in ${loadedState}` : ''
                  }.`}
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="search"
                value={filter}
                onChange={(e): void => setFilter(e.target.value)}
                placeholder="Filter by name, API, or county"
                style={{ ...fieldStyle, flex: 1 }}
              />
              <select
                value={countyFilter}
                onChange={(e): void => setCountyFilter(e.target.value)}
                style={{ ...fieldStyle, width: '180px' }}
                aria-label="Filter by county"
              >
                <option value="">All counties</option>
                {counties.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button
                type="button"
                variant="outline"
                onClick={(): void =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    for (const w of availableVisible) next.add(w.id);
                    return next;
                  })
                }
              >
                Select All Available
              </Button>
              <Button type="button" variant="outline" onClick={(): void => setSelected(new Set())}>
                Deselect All
              </Button>
              {/* (a) Counter matches the current filter: selected here / available here. */}
              <span style={{ ...subText, marginLeft: 'auto' }}>
                {selected.size} selected ({availableVisible.length} available{countyLabel})
              </span>
            </div>

            {/* (b) Total across all filters — surfaced when a filter is active so the
                count above isn't mistaken for a filter-scoped number. */}
            {selected.size > 0 && filterActive && (
              <p style={subText}>
                {selected.size} well{selected.size === 1 ? '' : 's'} selected across all counties.
              </p>
            )}

            {/* (c) Warn when selected wells are hidden by the current filter. */}
            {hiddenSelected > 0 && (
              <p style={subText}>
                {hiddenSelected} additional well{hiddenSelected === 1 ? '' : 's'} selected from
                other filters.{' '}
                <button
                  type="button"
                  onClick={(): void => {
                    setCountyFilter('');
                    setFilter('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-accent-blue)',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                  }}
                >
                  Show all selected
                </button>
              </p>
            )}

            <div
              style={{
                maxHeight: '320px',
                overflowY: 'auto',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '6px',
              }}
            >
              {visibleWells.length === 0 && !wellsLoading && (
                <p style={{ ...subText, padding: '10px 12px' }}>No wells match the filter.</p>
              )}
              {visibleWells.map((w) => (
                <WellSelectRow
                  key={w.id}
                  well={w}
                  checked={selected.has(w.id)}
                  onToggle={(): void => toggle(w.id)}
                />
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={(): void => setStep(1)}>
                Back
              </Button>
              <Button
                type="button"
                disabled={selected.size === 0}
                onClick={(): void => setStep(3)}
                style={primaryBtn}
              >
                Next
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── STEP 3 — optional tank ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={applyTank}
                onChange={(e): void => setApplyTank(e.target.checked)}
              />
              Apply a standard tank to all selected wells
            </label>

            {applyTank ? (
              <>
                <FormRow label="Standard tank (optional — auto-fills)" htmlFor="bulk-std">
                  <Combobox
                    id="bulk-std"
                    options={standardTanks.map((s) => ({
                      value: s.id,
                      label: standardTankLabel(s),
                    }))}
                    value={standardId}
                    onChange={onPickStandard}
                    placeholder="Pick from catalog…"
                    searchPlaceholder="Search standard tanks…"
                    emptyText="No standard tanks."
                  />
                </FormRow>
                <FormRow label="Tank name" htmlFor="bulk-tank-name" required>
                  <input
                    id="bulk-tank-name"
                    value={tankName}
                    onChange={(e): void => setTankName(e.target.value)}
                    placeholder="e.g. Tank 1"
                    style={fieldStyle}
                  />
                </FormRow>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <FormRow label="Capacity (bbl)" htmlFor="bulk-cap">
                    <input
                      id="bulk-cap"
                      inputMode="decimal"
                      value={capacity}
                      onChange={(e): void => setCapacity(e.target.value)}
                      style={fieldStyle}
                    />
                  </FormRow>
                  <FormRow label="BBL per inch" htmlFor="bulk-bpi">
                    <input
                      id="bulk-bpi"
                      inputMode="decimal"
                      value={bblPerInch}
                      onChange={(e): void => setBblPerInch(e.target.value)}
                      style={fieldStyle}
                    />
                  </FormRow>
                </div>
                <FormRow label="Tank type" htmlFor="bulk-type">
                  <input
                    id="bulk-type"
                    value={tankType}
                    onChange={(e): void => setTankType(e.target.value)}
                    placeholder="e.g. GUN BARREL or WATER"
                    style={fieldStyle}
                  />
                </FormRow>
                <p style={{ ...labelStyle, textTransform: 'none' }}>
                  This tank will be applied to all {selected.size} selected wells.
                </p>
                {formError !== null && (
                  <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
                    {formError}
                  </p>
                )}
              </>
            ) : (
              <p style={subText}>
                Wells will be imported with no tanks. You can add tanks to individual wells later.
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={(): void => setStep(2)}>
                Back
              </Button>
              <Button
                type="button"
                onClick={(): void => {
                  if (applyTank && tankName.trim() === '') {
                    setFormError('Tank name is required when applying a tank.');
                    return;
                  }
                  setFormError(null);
                  setStep(4);
                }}
                style={primaryBtn}
              >
                Review
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── STEP 4 — confirm + import / result ── */}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {result === null ? (
              <>
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border-secondary)',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                    Import {selected.size} well{selected.size === 1 ? '' : 's'} for {operatorName}
                  </div>
                  <div style={subText}>
                    {applyTank
                      ? `${selected.size} well${selected.size === 1 ? '' : 's'} will receive a ${
                          tankName.trim() || 'standard'
                        } tank.`
                      : 'Wells will be imported with no tanks.'}
                  </div>
                  <div style={{ ...subText, marginTop: '4px' }}>
                    Wells already added or already in a battery are skipped automatically.
                  </div>
                </div>
                {importError !== null && (
                  <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
                    {importError}
                  </p>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={(): void => setStep(3)}>
                    Back
                  </Button>
                  <Button type="button" disabled={importing} onClick={doImport} style={primaryBtn}>
                    {importing
                      ? `Importing ${formatNumber(progress.done)} of ${formatNumber(progress.total)} wells…`
                      : 'Import'}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div
                  style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  {result.imported} well{result.imported === 1 ? '' : 's'} imported successfully
                </div>
                {result.skipped > 0 && (
                  <div style={subText}>
                    {result.skipped} skipped:
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                      {result.skippedReasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(): void => {
                      reset();
                    }}
                  >
                    Import More
                  </Button>
                  <Button
                    type="button"
                    onClick={(): void => onOpenChange(false)}
                    style={primaryBtn}
                  >
                    View Wells
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const primaryBtn: React.CSSProperties = {
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
  borderColor: 'var(--color-accent-blue)',
};

function WellSelectRow({
  well,
  checked,
  onToggle,
}: {
  well: OperatorWell;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const disabled = well.hasHaulerWells || well.inTankBattery;
  // "Already added" = in this hauler's catalog (with or without tanks); the tank
  // suffix is informational (DEC-105).
  const status = well.inTankBattery
    ? 'In battery'
    : well.hasHaulerWells
      ? well.hasHaulerTanks
        ? 'Already added · has tanks'
        : 'Already added'
      : 'Available';
  const statusColor = well.inTankBattery
    ? 'var(--color-warning)'
    : well.hasHaulerWells
      ? 'var(--color-text-tertiary)'
      : 'var(--color-success)';
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 12px',
        borderBottom: '1px solid var(--color-border-secondary)',
        fontSize: '14px',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{well.wellName}</div>
        <div style={subText}>
          API {well.apiNumber}
          {well.county != null ? ` · ${well.county}` : ''}
          {well.wellType != null ? ` · ${well.wellType}` : ''}
        </div>
      </div>
      <span style={{ fontSize: '11px', fontWeight: 600, color: statusColor }}>{status}</span>
    </label>
  );
}
